import type {
  BoardSwitcherNavItem,
  BoardSwitcherSection,
} from '@trello/board-switcher';

export const getNavItemElement = (
  section: BoardSwitcherSection | null | undefined,
  id: string | null | undefined,
) =>
  section && id
    ? // eslint-disable-next-line @trello/no-query-selector
      document.querySelector(
        `[data-nav-item][data-section="${section}"][data-id="${id}"]`,
      )
    : null;

export const getNodeData = (node: Node | null | undefined) => {
  const element = node as HTMLElement;
  const data = element?.dataset;

  if (!data?.navItem) {
    return null;
  }

  // eslint-disable-next-line @trello/no-query-selector
  const link = element.tagName === 'A' ? element : element?.querySelector('a');
  const href = link ? link.getAttribute('href') : null;

  return {
    type: data.navItem as BoardSwitcherNavItem,
    section: data.section as BoardSwitcherSection,
    id: data.id as string,
    href,
  };
};

/**
 * Creates a TreeWalker of all the nav item elements in the BoardSwitcher.
 * This can be used for easily navigating from board tile to board tile.
 */
export const getNavItemTreeWalker = (
  currentSection?: BoardSwitcherSection | null,
  currentId?: string | null,
) => {
  const walker = document.createTreeWalker(
    document.getElementById('board-switcher')!,
    NodeFilter.SHOW_ELEMENT,
    (node) => {
      // Don't search within elements; skip to the next one.
      if (node.parentNode && getNodeData(node.parentNode)) {
        return NodeFilter.FILTER_REJECT;
      }
      return getNodeData(node)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    },
  );

  const currentNavItem = getNavItemElement(currentSection, currentId);
  if (currentNavItem) {
    walker.currentNode = currentNavItem;
  }
  return walker;
};

export const getFirstNavItem = () => {
  const walker = getNavItemTreeWalker();
  const navItem = walker.firstChild();
  const data = getNodeData(navItem);

  if (!data) {
    return null;
  }

  return {
    activeId: data.id,
    activeSection: data.section,
  };
};

export const getNthSiblingNavItem = (
  activeSection: BoardSwitcherSection | null,
  activeId: string | null,
  skip: number,
  dir: 'next' | 'previous',
) => {
  const walker = getNavItemTreeWalker(activeSection, activeId);

  const currentData = getNodeData(walker.currentNode);
  const type = currentData?.type;

  // If we're currently on an expand button, we only want to skip one sibling.
  const n = type === 'expand-button' ? 1 : skip;

  let sibling: Node | null = null;
  for (let i = 0; i < n; i++) {
    const newSibling =
      dir === 'next' ? walker.nextSibling() : walker.previousSibling();

    if (!newSibling) {
      break;
    }
    sibling = newSibling;

    // If we've reached a different type of nav item, stop and don't skip any
    // additional siblings.
    const newSiblingData = getNodeData(newSibling);
    if (!newSibling || newSiblingData?.type !== type) {
      break;
    }
  }

  const data = getNodeData(sibling);

  if (!data) {
    return null;
  }

  return {
    activeId: data.id,
    activeSection: data.section,
  };
};

export const getFirstSectionNavItem = (targetSection: BoardSwitcherSection) => {
  const walker = getNavItemTreeWalker();

  let currentNode: Node | null = walker.currentNode;

  while (currentNode) {
    const nodeData = getNodeData(currentNode);
    if (nodeData && nodeData.section === targetSection) {
      break;
    }
    currentNode = walker.nextNode();
  }

  const data = getNodeData(currentNode);

  if (!data) {
    return null;
  }

  return {
    activeId: data.id,
    activeSection: data.section,
  };
};
